import React, { useContext, useState, useEffect } from 'react';
import Box from '@nubank/nuds-web/components/Box/Box';
import { NuDsContext } from '@nubank/nuds-web/components/NuDSProvider/NuDSProvider';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import Collapse from '@nubank/nuds-web/components/Collapse/Collapse';
import styled from 'styled-components';
import Icon from '@nubank/nuds-web/components/Icon/Icon';

import { CollapseHeader } from './StyledCollapseHeader';

const StyledTypography = styled(Typography)`
  background: linear-gradient(87deg, #9F2DEB 0%, #6200A3 100%);
  /* stylelint-disable property-no-vendor-prefix, property-no-unknown */
  -webkit-text-fill-color: transparent;
  -webkit-background-clip: text;
  background-clip: text;
  height: 100%;
`;

const StyledDesktopBox = styled(Box)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 100px;
  grid-row-gap: 0;
  padding: 20px;
  grid-auto-rows: max-content;
  
  @media (width < 1151px) {
    display: none;
  }
`;

const StyledAnswerTypography = styled(Typography)`
  a {
    text-decoration: underline;
  }

  p {
    margin-bottom: 20px;
  }
`;

export default function PopularQuestionsList() {
  const { formatMessage } = useContext(NuDsContext);
  const initialQuestions = formatMessage({ id: 'cms.questions' });
  const allQuestions = formatMessage({ id: 'cms.allQuestions' });

  const [displayedQuestions, setDisplayedQuestions] = useState([]);
  const [startIndex, setStartIndex] = useState(0);

  // Use useEffect to avoid SSR mismatch
  useEffect(() => {
    if (initialQuestions && Array.isArray(initialQuestions)) {
      setDisplayedQuestions(initialQuestions);
    }
  }, [initialQuestions]);

  const loadMoreQuestions = () => {
    // Calculate the next set of questions to display
    const nextQuestionCount = startIndex + initialQuestions.length + 10;
    const nextQuestions = allQuestions
      .slice(startIndex + initialQuestions.length, nextQuestionCount)
      .filter(question => !displayedQuestions.some(q => q.id === question.id));

    // Update the displayed questions
    setDisplayedQuestions(prevQuestions => [
      ...prevQuestions,
      ...nextQuestions,
    ]);
    setStartIndex(prevIndex => prevIndex + 10);
  };

  return (
    <Box
      tag="section"
      id="popular-questions"
      backgroundColor="white"
      paddingTop={{ xs: '137px', lg: '117px' }}
      paddingBottom={{ xs: '216px', lg: '239px' }}
    >
      <Box
        marginBottom={{ xs: '78px', lg: '70px' }}
        display="flex"
        justifyContent="center"
      >
        <Typography variant="heading2" marginRight="8px">
          Preguntas
          {' '}
          <StyledTypography variant="heading2" tag="span" color="primary">
            Populares
          </StyledTypography>
        </Typography>
      </Box>

      {/* MOBILE LIST */}

      <Box
        paddingLeft={{ xs: '25px' }}
        paddingRight={{ xs: '40px' }}
        maxWidth="600px"
        margin="0 auto"
        display={{ xs: 'block', lg: 'none' }}
      >
        {
          displayedQuestions.map((item, index) => (
            <Box
              marginTop={{ xs: '16x', md: 60, lg: 56 }}
              key={item.id}
            >
              <Collapse
                id={item.id}
                initiallyOpen={index === 0}
              >
                <CollapseHeader>
                  {item.question}
                </CollapseHeader>
                <Collapse.Body>
                  <StyledAnswerTypography
                    variant="subtitle1"
                    marginTop="2x"
                    dangerouslySetInnerHTML={{
                      __html: item.shortAnswer,
                    }}
                  />
                </Collapse.Body>
              </Collapse>
            </Box>
          ))
        }
      </Box>

      {/* DESKTOP LIST */}

      <StyledDesktopBox maxWidth="1200px" margin="0 auto">
        {displayedQuestions.map(item => (
          <Box key={item.id} minHeight="200px">
            <Collapse id={item.id}>
              <CollapseHeader>
                {item.question}
              </CollapseHeader>
              <Collapse.Body>
                <StyledAnswerTypography
                  variant="subtitle1"
                  paddingBottom="12x"
                  dangerouslySetInnerHTML={{
                    __html: item.shortAnswer,
                  }}
                />
              </Collapse.Body>
            </Collapse>
          </Box>
        ))}
      </StyledDesktopBox>

      {/* SEE MORE QUESTIONS */}
      {startIndex + initialQuestions.length < allQuestions.length && (
        <Box
          id="see-more-questions"
          display="flex"
          justifyContent={{ xs: 'left', md: 'center' }}
          paddingLeft={{ xs: '25px', md: '0' }}
          paddingRight={{ xs: '40px', md: '0' }}
          marginTop={{ xs: '65px', md: '50px' }}
          alignItems="center"
          onClick={loadMoreQuestions}
          style={{ cursor: 'pointer' }}
        >
          <Typography strong color="primary" marginRight="2x">
            Ver más preguntas
          </Typography>
          <Icon name="chevron-down" color="primary" />
        </Box>
      )}
    </Box>
  );
}
